import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { Meeting } from '@/types/meetings';
import { MeetingsService } from '@/services/meetingsService';
import { getMeetingTimeStatus, formatDisplayTime, isMeetingLive } from '@/utils/meetingUtils';
import { normalizeWeekdayValue } from '@/utils/meetingLocale';
import { useLanguage } from '@/contexts/LanguageContext';
import meetingStatsService, { MeetingAttendee } from '@/services/meetingStatsService';
import Avatar from '@/components/Avatar';
import { apiService } from '@/services/apiService';

const CARD_WIDTH = 255;
const CARD_GAP = 8;
const SNAP_INTERVAL = CARD_WIDTH + CARD_GAP;

function parseTimeToMinutes(timeString: string): number {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
}

function getDayIndex(dayName: string): number {
  const normalized = normalizeWeekdayValue(dayName);
  const dayMap: { [key: string]: number } = {
    sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
    thursday: 4, friday: 5, saturday: 6,
  };
  return dayMap[normalized] ?? 0;
}

function getNextMeetings(allMeetings: Meeting[]): Meeting[] {
  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  const currentDayIndex = now.getDay();

  const withNext = allMeetings.map(meeting => {
    const meetingTime = parseTimeToMinutes(meeting.meeting_time);
    const meetingDays = meeting.meeting_weekday.split(';').map(d => d.trim());
    let nextOccurrence = Infinity;

    meetingDays.forEach(day => {
      const dayIdx = getDayIndex(day);
      let daysUntil = (dayIdx - currentDayIndex + 7) % 7;

      if (daysUntil === 0 && meetingTime > currentTime) {
        nextOccurrence = Math.min(nextOccurrence, meetingTime);
      } else if (daysUntil === 0 && meetingTime <= currentTime) {
        daysUntil = 7;
        nextOccurrence = Math.min(nextOccurrence, daysUntil * 24 * 60 + meetingTime);
      } else {
        nextOccurrence = Math.min(nextOccurrence, daysUntil * 24 * 60 + meetingTime);
      }
    });

    return { ...meeting, nextOccurrence };
  });

  return withNext
    .filter(m => m.nextOccurrence !== Infinity)
    .sort((a, b) => {
      const aTime = parseTimeToMinutes(a.meeting_time);
      const bTime = parseTimeToMinutes(b.meeting_time);
      const currentMins = now.getHours() * 60 + now.getMinutes();
      const aIsLive = currentMins - aTime >= 0 && currentMins - aTime <= 120;
      const bIsLive = currentMins - bTime >= 0 && currentMins - bTime <= 120;
      if (aIsLive && !bIsLive) return -1;
      if (!aIsLive && bIsLive) return 1;
      return a.nextOccurrence - b.nextOccurrence;
    })
    .slice(0, 5);
}

const AttendeeAvatars = ({ attendees }: { attendees: MeetingAttendee[] }) => (
  <View style={styles.avatarGroup}>
    {attendees.slice(0, 3).map((attendee, index) => (
      <View key={`${attendee.userId}-${index}`} style={{ marginLeft: index > 0 ? -8 : 0 }}>
        <Avatar
          uri={attendee.profilePhotoUrl}
          size={32}
          borderColor="#F9F8F3"
          borderWidth={3}
          useCache={false}
        />
      </View>
    ))}
    {Array.from({ length: Math.max(0, 3 - attendees.length) }).map((_, index) => (
      <View key={`ph-${index}`} style={{ marginLeft: attendees.length + index > 0 ? -8 : 0 }}>
        <Avatar
          size={32}
          borderColor="#F9F8F3"
          borderWidth={3}
        />
      </View>
    ))}
  </View>
);

export default function CommunityMeetingsSection({ userId }: { userId?: string } = {}) {
  const router = useRouter();
  const { t } = useLanguage();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [attendeesMap, setAttendeesMap] = useState<{ [meetingId: string]: MeetingAttendee[] }>({});

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        let next: Meeting[] = [];

        if (userId) {
          const response = await apiService.get<{ success: boolean; meetings: Meeting[] }>(
            `/api/checkins/user/${userId}/recent`
          );
          console.log(`[CommunityMeetings] raw response:`, JSON.stringify({ success: response?.success, hasData: !!response?.data, meetings: response?.data?.meetings?.length, error: response?.error, status: response?.status }));
          next = response?.data?.meetings || [];
          console.log(`[CommunityMeetings] userId=${userId.substring(0, 8)} meetings=${next.length}`);
        } else {
          const all = await MeetingsService.getAllMeetings();
          // Live Now section: only render meetings that are currently
          // ongoing. If none are live, the section hides itself via the
          // `meetings.length === 0 → return null` guard below.
          next = getNextMeetings(all).filter((m) =>
            m.meeting_time ? isMeetingLive(m.meeting_time, m.meeting_weekday, t) : false
          );
        }

        if (cancelled) return;
        setMeetings(next);

        if (next.length > 0) {
          const ids = next.map(m => m.id);
          try {
            const stats = await meetingStatsService.getBatchMeetingStats(ids);
            if (cancelled) return;
            const map: { [id: string]: MeetingAttendee[] } = {};
            for (const id of ids) {
              map[id] = stats[id]?.recentAttendees ?? [];
            }
            setAttendeesMap(map);
          } catch {
          }
        }
      } catch (err) {
        console.error('[CommunityMeetings] Error loading:', err);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId]);

  if (meetings.length === 0) return null;

  const isProfileMode = !!userId;

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <View style={styles.iconBadge}>
          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <Path d="M12.2788 9.40747C12.2788 9.40747 6.13995 10.3111 5.36934 10.3915C4.59874 10.4719 3.37862 9.8674 2.94467 8.67447C2.51072 7.4816 3.05624 6.23425 3.6984 5.80095C4.34058 5.367 9.59856 2.12891 9.59856 2.12891" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <Path d="M5.53125 10.3699L6.51526 13.0735C6.7812 13.8039 7.58813 14.1808 8.31853 13.9142C9.04893 13.6489 9.42513 12.8413 9.1598 12.1116L8.39313 10.0054" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <Path fillRule="evenodd" clipRule="evenodd" d="M12.7364 5.11026C13.472 7.1308 13.3325 9.0372 12.4244 9.3674C11.5162 9.6982 10.1839 8.3276 9.44829 6.30703C8.71275 4.28582 8.85289 2.38007 9.76035 2.04925C10.6684 1.71844 12.0008 3.08905 12.7364 5.11026Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </Svg>
        </View>
        <Text style={styles.sectionTitle}>
          {isProfileMode ? t('communityProfile.recentlyParticipated') : t('communityProfile.liveNow')}
        </Text>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        style={styles.scrollView}
        snapToInterval={SNAP_INTERVAL}
        decelerationRate="fast"
        snapToAlignment="start"
      >
        {meetings.map(meeting => {
          const timeStatus = getMeetingTimeStatus(meeting.meeting_time, meeting.meeting_weekday, t);
          const isLive = timeStatus.status === 'ongoing';
          const attendees = attendeesMap[meeting.id] ?? [];

          return (
            <TouchableOpacity
              key={meeting.id}
              style={styles.card}
              activeOpacity={0.75}
              onPress={() => router.push(`/meeting/${meeting.id}`)}
            >
              <View style={styles.cardHeader}>
                <View style={styles.cardHeaderContent}>
                  <Text style={styles.meetingName} numberOfLines={2}>
                    {meeting.meeting_name}
                  </Text>
                  <View style={styles.timeRow}>
                    <View style={styles.timeLeft}>
                      <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <Path fillRule="evenodd" clipRule="evenodd" d="M10.4566 3.37598L4.94039 3.37652C3.32005 3.37652 2.30859 4.27884 2.30859 5.93735V10.9302C2.30859 12.6149 3.32005 13.5381 4.94039 13.5381L10.4519 13.5376C12.077 13.5376 13.0837 12.6305 13.0837 10.9714V5.9368C13.0885 4.27829 12.0818 3.37598 10.4566 3.37598Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <Path d="M5.01562 7.625H5.02229" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <Path d="M5.01562 9.92773H5.02229" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <Path d="M7.69141 7.625H7.69807" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <Path d="M7.69141 9.92773H7.69807" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <Path d="M10.3594 7.625H10.366" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <Path d="M10.3594 9.92773H10.366" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <Path d="M10.1016 2.46143V4.44497" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <Path d="M5.28516 2.46143V4.44497" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </Svg>
                      <Text style={styles.timeText}>
                        {isLive ? t('communityProfile.live') : `${formatDisplayTime(meeting.meeting_time)}`}
                      </Text>
                    </View>
                    {!isLive && timeStatus.message ? (
                      <Text style={styles.countdownText}>{timeStatus.message}</Text>
                    ) : null}
                  </View>
                </View>
              </View>

              <View style={styles.cardFooter}>
                <AttendeeAvatars attendees={attendees} />
                <View style={styles.arrowButton}>
                  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <Path d="M5.83203 3.3335L10.4987 8.00016L5.83203 12.6668" stroke="#151515" strokeWidth="2" strokeLinecap="square"/>
                  </Svg>
                </View>
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    paddingTop: 24,
    paddingBottom: 24,
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#F9F8F3',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingHorizontal: 24,
  },
  iconBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 14,
  },
  scrollView: {
    marginLeft: 0,
  },
  scrollContent: {
    flexDirection: 'row',
    gap: CARD_GAP,
    paddingHorizontal: 24,
    paddingRight: 24,
  },
  card: {
    width: CARD_WIDTH,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: 24,
    paddingBottom: 16,
    minHeight: 98,
    justifyContent: 'flex-end',
  },
  cardHeaderContent: {
    gap: 12,
  },
  meetingName: {
    fontSize: 16,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 22.4,
  },
  timeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  timeLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  timeText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 12,
  },
  countdownText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#7E7D75',
    lineHeight: 12,
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  avatarGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  arrowButton: {
    width: 32,
    height: 32,
    borderRadius: 12,
    backgroundColor: '#F9F8F3',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
